/**
 * Settings Hook
 * Manages user preferences and app configuration
 */

import { useState, useEffect } from 'react';
import { logger } from '../utils/logger';

export interface Settings {
  general: {
    defaultProject?: string;
    autoSaveInterval: number; // milliseconds (0 = off)
    dateFormat: string;
    timeFormat: '12h' | '24h';
    language: string;
  };
  appearance: {
    theme: 'light' | 'dark' | 'auto';
    compactMode: boolean;
    showGridLines: boolean;
    fontSize: number;
    sidebarPosition: 'left' | 'right';
    primaryColor: string;
  };
  notifications: {
    desktop: boolean;
    email: boolean;
    projectUpdates: boolean;
    testCompletions: boolean;
    comments: boolean;
    sound: string;
  };
  advanced: {
    developerMode: boolean;
    experimentalFeatures: boolean;
    apiEndpoint?: string;
    debugLogging: boolean;
    performanceMonitoring: boolean;
  };
}

export const DEFAULT_SETTINGS: Settings = {
  general: {
    autoSaveInterval: 60000, // 1 minute
    dateFormat: 'MM/DD/YYYY',
    timeFormat: '12h',
    language: 'en',
  },
  appearance: {
    theme: 'light',
    compactMode: false,
    showGridLines: true,
    fontSize: 14,
    sidebarPosition: 'left',
    // A11y cheap-wins — this value is applied to `--primary-color`, which
    // `a:not(.btn) { color: var(--primary-color) }` (App.css) uses for every
    // plain export/action link app-wide. The old Blue-500 (#3b82f6) measured
    // ~3.68:1 on white — below WCAG AA's 4.5:1 for normal text. Blue-600
    // (#2563eb) clears ~5.17:1 on white / ~4.95:1 on the app's off-white
    // page background, same hue, one step darker.
    primaryColor: '#2563eb',
  },
  notifications: {
    desktop: false,
    email: true,
    projectUpdates: true,
    testCompletions: true,
    comments: true,
    sound: 'default',
  },
  advanced: {
    developerMode: false,
    experimentalFeatures: false,
    debugLogging: false,
    performanceMonitoring: false,
  },
};

const STORAGE_KEY = 'fmea-settings';

function loadSettings(): Settings {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Merge with defaults to handle new settings
      return {
        general: { ...DEFAULT_SETTINGS.general, ...parsed.general },
        appearance: { ...DEFAULT_SETTINGS.appearance, ...parsed.appearance },
        notifications: { ...DEFAULT_SETTINGS.notifications, ...parsed.notifications },
        advanced: { ...DEFAULT_SETTINGS.advanced, ...parsed.advanced },
      };
    }
  } catch (error) {
    logger.error('Failed to load settings:', error);
  }
  return DEFAULT_SETTINGS;
}

function saveSettings(settings: Settings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch (error) {
    logger.error('Failed to save settings:', error);
  }
}

export function useSettings() {
  const [settings, setSettings] = useState<Settings>(loadSettings());

  const updateSetting = <C extends keyof Settings, K extends keyof Settings[C]>(
    category: C,
    key: K,
    value: Settings[C][K]
  ) => {
    const updated = {
      ...settings,
      [category]: { ...settings[category], [key]: value },
    };
    setSettings(updated);
    saveSettings(updated);
  };

  const updateCategory = <C extends keyof Settings>(
    category: C,
    values: Partial<Settings[C]>
  ) => {
    const updated = {
      ...settings,
      [category]: { ...settings[category], ...values },
    };
    setSettings(updated);
    saveSettings(updated);
  };

  const resetSettings = () => {
    setSettings(DEFAULT_SETTINGS);
    saveSettings(DEFAULT_SETTINGS);
  };

  const exportSettings = () => {
    const dataStr = JSON.stringify(settings, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);
    const exportFileDefaultName = `fmea-settings-${new Date().toISOString().split('T')[0]}.json`;

    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  const importSettings = (jsonString: string) => {
    try {
      const imported = JSON.parse(jsonString);
      const merged = {
        general: { ...DEFAULT_SETTINGS.general, ...imported.general },
        appearance: { ...DEFAULT_SETTINGS.appearance, ...imported.appearance },
        notifications: { ...DEFAULT_SETTINGS.notifications, ...imported.notifications },
        advanced: { ...DEFAULT_SETTINGS.advanced, ...imported.advanced },
      };
      setSettings(merged);
      saveSettings(merged);
      return true;
    } catch (error) {
      logger.error('Failed to import settings:', error);
      return false;
    }
  };

  return {
    settings,
    updateSetting,
    updateCategory,
    resetSettings,
    exportSettings,
    importSettings,
  };
}
